import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const formSchema = z.object({
  statusName: z.string().min(1, "Status name is required"),
});

type FormData = z.infer<typeof formSchema>;

interface PatientProgramStatusFormProps {
  initialData?: {
    statusName: string;
  };
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isEdit: boolean;
  isSubmitting?: boolean;
}

export const PatientProgramStatusForm = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  isEdit,
  isSubmitting = false
}: PatientProgramStatusFormProps) => {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      statusName: initialData?.statusName || "",
    },
  });

  const watchedStatusName = form.watch("statusName");

  // Create stable reference for initial data comparison
  const initialDataRef = useMemo(() => ({
    statusName: initialData?.statusName || "",
  }), []);

  // Check if form has changes (for edit mode)
  const hasFormChanges = useCallback(() => {
    if (!isEdit) return true; // Always allow save in add mode

    const currentStatusName = watchedStatusName?.trim() || '';
    const initialStatusName = initialDataRef.statusName.trim();
    
    return currentStatusName !== initialStatusName;
  }, [watchedStatusName, isEdit, initialDataRef]);

  const formHasChanges = hasFormChanges();

  return (
    <div className="container mx-auto py-6">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl">
            {isEdit ? "Edit Patient Program Status" : "Add Patient Program Status"}
          </CardTitle>
          <CardDescription>
            {isEdit 
              ? "Update the patient program status information below." 
              : "Fill in the information below to create a new patient program status."
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="statusName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter status name" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4 justify-end">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  variant="default"
                  disabled={isSubmitting || (isEdit && !formHasChanges)}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update Status" : "Save Status"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};